import Quill from 'quill';

export interface ISelection {
  index: number;
}

class MarkdownShortcuts {
  public quill: Quill;
  public ignoreTags: string[];
  public matches: any;

  constructor(quill: Quill) {
    this.quill = quill;

    this.ignoreTags = ['PRE'];
    this.matches = [
      {
        name: 'header',
        pattern: /^(#){1,3}\s/g,
        action: (text: string, selection: ISelection, pattern: RegExp) => {
          const match = pattern.exec(text);
          if (!match) return;
          const size = match[0].length;
          // Need to defer this action https://github.com/quilljs/quill/issues/1134

          setTimeout(() => {
            this.quill.formatLine(selection.index, 0, 'header', size - 1);
            this.quill.deleteText(selection.index - size, size);
          }, 0);
        }
      },
      {
        name: 'blockquote',
        pattern: /^(>)\s/g,
        action: (text: string, selection: ISelection) => {
          // Need to defer this action https://github.com/quilljs/quill/issues/1134
          setTimeout(() => {
            this.quill.formatLine(selection.index, 1, 'blockquote', true);
            this.quill.deleteText(selection.index - 2, 2);
          }, 0);
        }
      },
      {
        name: 'code-block',
        pattern: /^`{3}(?:\s|\n)/g,
        action: (text: string, selection: ISelection) => {
          // Need to defer this action https://github.com/quilljs/quill/issues/1134
          setTimeout(() => {
            this.quill.formatLine(selection.index, 1, 'code-block', true);
            this.quill.deleteText(selection.index - 4, 4);
          }, 0);
        }
      },
      {
        name: 'bold',
        pattern: /(?:\*|_){2}(.+?)(?:\*|_){2}/g,
        action: (text: string, selection: ISelection, pattern: RegExp, lineStart: number) => {
          const match = pattern.exec(text);

          if (!match) {
            return;
          }

          const annotatedText = match[0];
          const matchedText = match[1];
          const startIndex = lineStart + match.index;

          if (text.match(/^([*_ \n]+)$/g)) return;

          setTimeout(() => {
            this.quill.deleteText(startIndex, annotatedText.length);
            this.quill.insertText(startIndex, matchedText, { bold: true });
            this.quill.format('bold', false);
          }, 0);
        }
      },
      {
        name: 'italic',
        pattern: /(?:\*){1}(.+?)(?:\*){1}/g,
        action: (text: string, selection: ISelection, pattern: RegExp, lineStart: number) => {
          const match = pattern.exec(text);

          if (!match) {
            return;
          }

          const annotatedText = match[0];
          const matchedText = match[1];
          const startIndex = lineStart + match.index;

          if (text.match(/^([*_ \n]+)$/g)) return;

          setTimeout(() => {
            this.quill.deleteText(startIndex, annotatedText.length);
            this.quill.insertText(startIndex, matchedText, { italic: true });
            this.quill.format('italic', false);
          }, 0);
        }
      },
      {
        name: 'strikethrough',
        pattern: /(?:~~)(.+?)(?:~~)/g,
        action: (text: string, selection: ISelection, pattern: RegExp, lineStart: number) => {
          const match = pattern.exec(text);

          if (!match) {
            return;
          }

          const annotatedText = match[0];
          const matchedText = match[1];
          const startIndex = lineStart + match.index;

          if (text.match(/^([*_ \n]+)$/g)) return;

          setTimeout(() => {
            this.quill.deleteText(startIndex, annotatedText.length);
            this.quill.insertText(startIndex, matchedText, { strike: true });
            this.quill.format('strike', false);
          }, 0);
        }
      },
      {
        name: 'hr',
        pattern: /^([-*]\s?){3}/g,
        action: (text: string, selection: ISelection) => {
          const startIndex = selection.index - text.length;
          setTimeout(() => {
            this.quill.deleteText(startIndex, text.length);

            this.quill.insertEmbed(startIndex + 1, 'hr', true, 'user');
            this.quill.insertText(startIndex + 2, '\n', 'silent');
            this.quill.setSelection(startIndex + 2, 0, 'silent');
          }, 0);
        }
      }
    ];

    // Handler that looks for insert deltas that match specific characters
    this.quill.on('text-change', delta => {
      for (const item of delta.ops) {
        // 特殊的空格-ASCII码值160
        const emptyReg = /\u00A0| /;

        if (item.hasOwnProperty('insert')) {
          if (typeof item.insert === 'string' && emptyReg.test(item.insert)) {
            this.onSpace();
          } else if (item.insert === '\n') {
            this.onEnter();
          }
        }
      }
    });
  }

  public isValid(text: string, tagName: string) {
    return typeof text !== 'undefined' && text && this.ignoreTags.indexOf(tagName) === -1;
  }

  public onSpace() {
    const selection = this.quill.getSelection();
    if (!selection) return;
    const [line, offset] = this.quill.getLine(selection.index);
    const text = line.domNode.textContent;
    const lineStart = selection.index - offset;

    if (this.isValid(text, line.domNode.tagName)) {
      for (const match of this.matches) {
        const matchedText = text.match(match.pattern);
        if (matchedText) {
          // We need to replace only matched text not the whole line
          match.action(text, selection, match.pattern, lineStart);
          return;
        }
      }
    }
  }

  public onEnter() {
    const selection = this.quill.getSelection();
    if (!selection) return;
    const [line, offset] = this.quill.getLine(selection.index);
    const text = line.domNode.textContent + ' ';
    const lineStart = selection.index - offset;
    selection.length = selection.index++;
    if (this.isValid(text, line.domNode.tagName)) {
      for (const match of this.matches) {
        const matchedText = text.match(match.pattern);
        if (matchedText) {
          match.action(text, selection, match.pattern, lineStart);
          return;
        }
      }
    }
  }
}

export default MarkdownShortcuts;
